'use client'

import type { HeaderProps } from '@maestro/commerce-blocks/components/header/header.types'

import { useTranslations } from 'next-intl'
import React, { Suspense, useMemo } from 'react'

import { HeaderControls } from '@maestro/commerce-blocks/components/header-controls'
import { HeaderTopbar } from '@maestro/commerce-blocks/components/header-topbar'
import { useHeader } from '@maestro/commerce-blocks/components/header/batons/use-header'
import { Logo } from '@maestro/commerce-blocks/components/logo'
import {
  NavigationHeader,
  NavigationMobileToggleButton,
} from '@maestro/commerce-blocks/components/navigation-header'
import { SearchBar } from '@maestro/commerce-blocks/components/search-bar'
import { MobileNavigationProvider } from '@pearlconvert/ui/providers'

import '@maestro/commerce-blocks/components/header/styles/header.scss'

export default function Header(props: HeaderProps) {
  const { menu, mobileShortcuts, searchShortcuts } = props
  const {
    headerRef,
    handleSkipLinkKeyDown,
    isContentOnly,
    isDesktop,
    isTopbarEnabled,
  } = useHeader()
  const t = useTranslations('commerce-blocks.header')

  const logo = (
    <span className="header__logo">
      <Logo source="/assets/logo.svg" destination="/" />
    </span>
  )

  const navigation = (
    <NavigationHeader
      menu={menu}
      shortcuts={mobileShortcuts}
      customClassName="header__navigation"
    />
  )

  const search = useMemo(() => {
    if (isContentOnly) {
      return null
    }
    return (
      <div className="header__search">
        <Suspense fallback={<div className="searchbar__root" />}>
          <SearchBar searchShortcuts={searchShortcuts} />
        </Suspense>
      </div>
    )
  }, [isContentOnly, searchShortcuts])

  const controls = useMemo(() => {
    if (isContentOnly) {
      return null
    }
    return <HeaderControls />
  }, [isContentOnly])

  const controlsMobile = (
    <ul className="header__controls--mobile mobile-only">
      <li className="header__controls-item">
        <NavigationMobileToggleButton />
      </li>
    </ul>
  )

  return (
    <>
      <a
        href="#main__content"
        className="header__skip-link"
        onKeyDown={handleSkipLinkKeyDown}
      >
        {t('jump-to-content')}
      </a>

      <header ref={headerRef} className="header__root">
        {isTopbarEnabled && <HeaderTopbar />}
        <nav className="header__container" aria-label={t('main-navigation')}>
          <MobileNavigationProvider>
            {isDesktop ? (
              <>
                {logo}
                {navigation}
                {search}
                {controls}
              </>
            ) : (
              <>
                {logo}
                {controls}
                {controlsMobile}
                {navigation}
                {search}
              </>
            )}
          </MobileNavigationProvider>
        </nav>
      </header>
    </>
  )
}
